// Third party
import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';

// Hooks
import useBodyScrollLock from '@/hooks/useBodyScrollLock';
import useThemeVersion from '@/hooks/useThemeVersion';

// Utils
import { FormattedMessage } from '../../../../../utils/i18n';
import { Button } from '../../../../components/buttons';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';
import { Typography } from '@capintel/component-lib/typography';

type Props = {
  dark?: boolean;
  className?: string;
  modal: React.ReactNode;
  tooltipText?: string;
  disabled?: boolean;
};

const ComparisonTopBarSettingsMenuItem = (props: Props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { isLightVersion } = useThemeVersion();

  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!props.disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsOpen(false);
  };

  const handleContentClick = (e: React.MouseEvent) => {
    // Prevent any clicks inside the content from closing the modal
    e.stopPropagation();
  };

  const handleContentMouseDown = (e: React.MouseEvent) => {
    // Prevent mousedown events from propagating
    e.stopPropagation();
  };

  return (
    <>
      <div
        className={classNames('comparison-menu-item settings-menu-item', {
          disabled: props.disabled,
        })}
        onClick={handleClick}
        role="button"
        tabIndex={0}
      >
        <div className="menu-item-label">
          <FormattedMessage id="SiteNavigation.Settings" />
        </div>
        {props.disabled && (
          <div className="menu-item-warning">
            <i className="icon-ds-warning" />
            <span>
              <FormattedMessage id="Comparison.ReadOnlySettingsTooltip" />
            </span>
          </div>
        )}
      </div>
      {isOpen &&
        !props.disabled &&
        ReactDOM.createPortal(
          <div className="mobile-settings-overlay">
            <div ref={contentRef} className="mobile-settings-content" onClick={handleContentClick} onMouseDown={handleContentMouseDown}>
              <div className={classNames('mobile-settings-header comparison-top-bar-navigation', { 'is-light': isLightVersion })}>
                <ul className="top-bar-nav-left">
                  <li>
                    <Button className="top-bar-button mobile-back-button" onClick={handleClose} icon="arrow-thin-left" aria-label="Back" />
                  </li>
                </ul>
                <Typography as={ALLOWED_HTML_TAGS.H2} variant="header2Xs">
                  <FormattedMessage id="SiteNavigation.Settings" />
                </Typography>
              </div>
              <div className="mobile-settings-body">{props.modal}</div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default ComparisonTopBarSettingsMenuItem;
